import React, { useState } from 'react';
import useSWR from 'swr';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { Shield as ShieldIcon } from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useUserActions } from 'shared/hooks/user.actions';

/** Shown to an account blocked for security: why, and a request to an administrator to reopen it. */
export default function AccountBlockedPage() {
  const { data, mutate } = useSWR('/account/block/', fetcher);
  const { logout } = useUserActions();
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const send = async () => {
    setBusy(true); setError(null);
    try {
      await axiosService.post('/account/block/', { message });
      mutate();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#f5f6fb', px: 2 }}>
      <Paper sx={{ maxWidth: 560, width: '100%', p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        {!data ? <Box display="grid" sx={{ placeItems: 'center', py: 4 }}><CircularProgress /></Box> : !data.blocked ? (
          <>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Your account is open</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>There is no block on your account.</Typography>
            <Button variant="contained" href="/">Go to Otable</Button>
          </>
        ) : (
          <>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <ShieldIcon sx={{ color: '#b91c1c', fontSize: 34 }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Your account is blocked</Typography>
            </Box>
            <Typography sx={{ mb: 1 }}>
              We blocked this account to protect it and the people on Otable: {data.reason.toLowerCase()}.
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
              Blocked since {new Date(data.since).toLocaleString()}. An administrator can reopen it.
            </Typography>
            {data.reopen_requested ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Your request to reopen the account was sent. An administrator will review it and you will hear from us by email.
              </Alert>
            ) : (
              <>
                <TextField fullWidth multiline minRows={4} label="Request to reopen your account" value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us briefly what happened, e.g. you mistyped the code sent to your other email address." />
                {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
                <Button variant="contained" sx={{ mt: 2 }} disabled={busy || !message.trim()} onClick={send}>
                  Send request
                </Button>
              </>
            )}
          </>
        )}
        <Box mt={3}>
          <Button size="small" color="inherit" onClick={logout}>Sign out</Button>
        </Box>
      </Paper>
    </Box>
  );
}
